import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { useUserStore } from '@/stores/userStore';
import { useAuthStore } from '@/auth/authStore';
import { authApi, AuthApiError, type AuthEnvelope, type ProviderAvailability } from '@/auth/authApi';
import { startNativeOAuth } from '@/auth/nativeOAuth';
import { startAppleSignIn, appleSignInSupported } from '@/auth/nativeApple';
import { EmailAuthModal } from '@/auth/EmailAuthModal';
import { Logo, Hold } from '@/art';
import { LangSwitcher } from '@/components/LangSwitcher';
import { useT } from '@/i18n/useI18n';
import { useAppVersion } from '@/version/appVersion';

type Provider = 'google' | 'apple' | 'email' | 'guest';

const DEFAULT_PROVIDERS: ProviderAvailability = {
  email: true,
  guest: true,
  google: false,
  apple: false,
  facebook: false,
  zalo: false,
  tiktok: false,
};

/** Open a URL in the system browser (Capacitor) or a new tab (web). */
async function openExternal(url: string): Promise<void> {
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.getPlatform() !== 'web') {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url });
      return;
    }
  } catch {
    /* fall through to window.open */
  }
  window.open(url, '_blank', 'noopener');
}

export function LoginScreen() {
  const t = useT();
  const appVersion = useAppVersion();
  const go = useUIStore((s) => s.go);
  const setProfile = useUserStore((s) => s.setProfile);
  const setSession = useAuthStore((s) => s.setSession);
  const [busy, setBusy] = useState<Provider | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [providers, setProviders] = useState<ProviderAvailability>(DEFAULT_PROVIDERS);

  useEffect(() => {
    authApi
      .providers()
      .then(setProviders)
      .catch(() => {
        // Server offline → keep defaults; the buttons that need OAuth will
        // still appear but show a clear error if tapped.
      });
  }, []);

  const adoptSession = (env: AuthEnvelope) => {
    setSession(env.token, env.user);
    setProfile({
      id: env.user.id,
      name: env.user.displayName,
      avatarColor: env.user.avatarColor,
      avatarUrl: env.user.avatarUrl,
      mmr: env.user.mmr,
      mmrSolo: env.user.mmrSolo ?? env.user.mmr,
      mmrDuo: env.user.mmrDuo ?? env.user.mmr,
      rank: env.user.rank,
      rankDivision: env.user.rankDivision,
      rankStars: env.user.rankStars,
      rankDuo: env.user.rankDuo ?? env.user.rank,
      rankDivisionDuo: env.user.rankDivisionDuo ?? env.user.rankDivision,
      rankStarsDuo: env.user.rankStarsDuo ?? env.user.rankStars,
      coins: env.user.coins,
      gems: env.user.gems,
      xp: env.user.xp,
      winStreak: env.user.winStreak,
      streakSolo: env.user.streakSolo ?? 0,
      streakDuo: env.user.streakDuo ?? 0,
      matchesPlayed: env.user.matchesPlayed,
      matchesWon: env.user.matchesWon,
    });
    go('home');
  };

  const handleOAuth = async (provider: 'google') => {
    if (!providers[provider]) {
      setError(t('login.oauth_not_configured', { provider: provider.toUpperCase() }));
      return;
    }
    setBusy(provider);
    // On web this navigates away and never returns; on native it opens the
    // system browser and resolves once the deep-link callback fires.
    const result = await startNativeOAuth(provider);
    if (!result.ok) {
      // Silent when the user simply cancelled the browser; only surface real
      // failures. Either way the button must un-stick.
      if (result.error !== 'cancelled') setError(t('login.server_unreachable'));
      setBusy(null);
      return;
    }
    try {
      const { user } = await authApi.me(result.token);
      adoptSession({ token: result.token, user });
    } catch {
      setError(t('login.server_unreachable'));
      setBusy(null);
    }
  };

  const handleApple = async () => {
    setBusy('apple');
    const result = await startAppleSignIn();
    if (!result.ok) {
      // Silent on user-cancel (Apple reports cancel as error 1001 / "canceled",
      // which doesn't contain the word "cancel"); show a clear error otherwise.
      if (!/cancel|canceled|1001|1000/i.test(result.error)) setError(result.error);
      setBusy(null);
      return;
    }
    try {
      const env = await authApi.appleNative({
        identityToken: result.identityToken,
        fullName: result.fullName,
        email: result.email,
      });
      adoptSession(env);
    } catch (e) {
      if (e instanceof AuthApiError) setError(e.message || e.code);
      else setError(t('login.server_unreachable'));
      setBusy(null);
    }
  };

  const handleGuest = async () => {
    if (busy) return;
    setBusy('guest');
    setError(null);
    try {
      const env = await authApi.guest();
      adoptSession(env);
    } catch (e) {
      if (e instanceof AuthApiError) setError(e.message || e.code);
      else setError(t('login.server_unreachable'));
      setBusy(null);
    }
  };

  const tryLogin = (provider: Provider) => {
    if (busy) return;
    setError(null);
    if (provider === 'email') {
      setEmailOpen(true);
      return;
    }
    if (provider === 'guest') {
      void handleGuest();
      return;
    }
    if (provider === 'apple') {
      void handleApple();
      return;
    }
    void handleOAuth(provider);
  };

  return (
    <div
      className="screen screen-fit scanlines"
      style={{
        background:
          'radial-gradient(ellipse at 30% 0%, rgba(155,93,229,0.28), transparent 55%),' +
          'radial-gradient(ellipse at 70% 100%, rgba(59,130,246,0.18), transparent 50%),' +
          'linear-gradient(180deg, var(--bg-mid), var(--bg-deep) 70%)',
        paddingTop: 'clamp(16px,5vh,48px)',
      }}
    >
      <div className="hex-bg" />

      <div style={{ position: 'absolute', top: 14, right: 14, zIndex: 10 }}>
        <LangSwitcher />
      </div>

      <FloatingHex color="red" top="14%" left="8%" size={38} delay={0} />
      <FloatingHex color="blue" top="22%" right="6%" size={52} delay={0.4} />
      <FloatingHex color="yellow" top="56%" left="2%" size={32} delay={0.8} />
      <FloatingHex color="purple" top="70%" right="10%" size={44} delay={1.2} />

      <div className="brand-bar" style={{ marginTop: 'clamp(8px,2vh,20px)' }} />

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: 'clamp(12px,3.5vh,40px)',
          marginBottom: 6,
          position: 'relative',
          zIndex: 2,
        }}
      >
        <Logo scale={1.05} />
      </div>

      <div
        style={{
          textAlign: 'center',
          padding: '0 30px',
          fontSize: 12,
          color: 'var(--ink-muted)',
          lineHeight: 1.5,
          marginTop: 'clamp(6px,1.6vh,14px)',
          marginBottom: 'clamp(8px,1.8vh,16px)',
          position: 'relative',
          zIndex: 2,
        }}
      >
        {t('login.tagline')} <br />
        <span style={{ color: 'var(--c-yellow)' }}>{t('login.tagline_winner')}</span>
      </div>

      {error && (
        <div
          style={{
            margin: '0 24px 12px',
            padding: '8px 12px',
            background: 'rgba(230,57,70,0.16)',
            border: '1px solid rgba(230,57,70,0.4)',
            borderRadius: 10,
            color: 'var(--c-red)',
            fontSize: 12,
            position: 'relative',
            zIndex: 2,
            textAlign: 'center',
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          padding: '0 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          position: 'relative',
          zIndex: 2,
        }}
      >
        {appleSignInSupported() && (
          <LoginButton
            busy={busy === 'apple'}
            onClick={() => tryLogin('apple')}
            icon={<ProviderIcon kind="apple" />}
            color="#000"
            textColor="#fff"
            label={t('login.continue_apple')}
          />
        )}
        <LoginButton
          busy={busy === 'google'}
          dimmed={!providers.google}
          onClick={() => tryLogin('google')}
          icon={<ProviderIcon kind="google" />}
          color="#fff"
          textColor="#1a1234"
          label={t('login.continue_google')}
        />
        <LoginButton
          bordered
          busy={busy === 'email'}
          onClick={() => tryLogin('email')}
          icon={<ProviderIcon kind="email" />}
          color="rgba(255,255,255,0.06)"
          textColor="var(--ink)"
          label={t('login.continue_email')}
        />
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: 'clamp(8px,2vh,20px) 30px clamp(6px,1.5vh,14px)',
          position: 'relative',
          zIndex: 2,
        }}
      >
        <div style={{ flex: 1, height: 1, background: 'var(--hairline)' }} />
        <span className="tag" style={{ color: 'var(--ink-dim)' }}>
          {t('login.or').toUpperCase()}
        </span>
        <div style={{ flex: 1, height: 1, background: 'var(--hairline)' }} />
      </div>

      <div style={{ padding: '0 24px', position: 'relative', zIndex: 2 }}>
        <button
          className="btn btn-ghost"
          onClick={() => tryLogin('guest')}
          disabled={busy === 'guest'}
          style={{
            width: '100%',
            padding: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            fontSize: 13,
            background: 'rgba(34,211,238,0.08)',
            borderColor: 'rgba(34,211,238,0.3)',
            color: 'var(--c-cyan)',
            cursor: busy === 'guest' ? 'wait' : 'pointer',
          }}
        >
          {busy === 'guest' ? <Spinner color="var(--c-cyan)" /> : <ProviderIcon kind="guest" />}
          <span>{busy === 'guest' ? t('login.creating_guest') : t('login.play_guest')}</span>
          {busy !== 'guest' && (
            <span
              style={{
                fontSize: 9,
                padding: '2px 5px',
                borderRadius: 3,
                background: 'rgba(34,211,238,0.2)',
                marginLeft: 4,
                letterSpacing: '0.06em',
              }}
            >
              {t('login.no_rewards')}
            </span>
          )}
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 8 }} />
      <div
        style={{
          padding: '14px 24px max(20px,env(safe-area-inset-bottom))',
          textAlign: 'center',
          fontSize: 10,
          color: 'var(--ink-dim)',
          lineHeight: 1.5,
          position: 'relative',
          zIndex: 2,
        }}
      >
        {t('login.terms_prefix')}{' '}
        <span
          role="button"
          tabIndex={0}
          onClick={() => void openExternal('https://dontmiss.games/terms.html')}
          style={{ color: 'var(--c-cyan)', textDecoration: 'underline', cursor: 'pointer' }}
        >
          {t('login.terms')}
        </span>{' '}
        &{' '}
        <span
          role="button"
          tabIndex={0}
          onClick={() => void openExternal('https://dontmiss.games/privacy.html')}
          style={{ color: 'var(--c-cyan)', textDecoration: 'underline', cursor: 'pointer' }}
        >
          {t('login.privacy')}
        </span>
        <div style={{ marginTop: 6, fontFamily: 'var(--f-mono)' }}>v{appVersion} · SEA-1</div>
      </div>

      {emailOpen && (
        <EmailAuthModal
          onClose={() => setEmailOpen(false)}
          onSuccess={(env) => {
            setEmailOpen(false);
            adoptSession(env);
          }}
        />
      )}
    </div>
  );
}

interface LoginButtonProps {
  icon: ReactNode;
  label: string;
  color: string;
  textColor: string;
  busy: boolean;
  onClick: () => void;
  compact?: boolean;
  bordered?: boolean;
  dimmed?: boolean;
}

function LoginButton({
  icon,
  label,
  color,
  textColor,
  busy,
  onClick,
  compact,
  bordered,
  dimmed,
}: LoginButtonProps) {
  const t = useT();
  const style: CSSProperties = {
    flex: compact ? 1 : undefined,
    minWidth: 0,
    padding: compact ? '12px 8px' : '14px 16px',
    borderRadius: 14,
    border: bordered ? '1px solid var(--hairline)' : 'none',
    background: color,
    color: textColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: compact ? 'center' : 'flex-start',
    gap: compact ? 6 : 12,
    cursor: busy ? 'wait' : 'pointer',
    font: 'inherit',
    fontWeight: 700,
    fontSize: compact ? 11 : 14,
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.15), 0 4px 0 rgba(0,0,0,0.35), 0 8px 16px rgba(0,0,0,0.25)',
    transition: 'transform 0.08s ease, opacity 0.2s ease',
    transform: busy ? 'translateY(2px)' : 'none',
    position: 'relative',
    overflow: 'hidden',
    letterSpacing: '0.01em',
    opacity: dimmed ? 0.55 : 1,
  };
  return (
    <button onClick={onClick} disabled={busy} style={style} title={dimmed ? t('login.not_configured') : label}>
      <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {busy ? <Spinner color={textColor} /> : icon}
      </div>
      <span
        style={{
          flex: compact ? undefined : 1,
          textAlign: compact ? 'center' : 'left',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </span>
      {!compact && !busy && <span style={{ fontSize: 14, opacity: 0.5 }}>›</span>}
    </button>
  );
}

function ProviderIcon({ kind }: { kind: Provider }) {
  switch (kind) {
    case 'google':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20">
          <circle cx="12" cy="12" r="11" fill="#fff" stroke="#e5e7eb" strokeWidth="0.5" />
          <text
            x="12"
            y="16.5"
            textAnchor="middle"
            fontSize="14"
            fontWeight="900"
            fontFamily="Manrope, sans-serif"
            fill="#4285f4"
          >
            G
          </text>
        </svg>
      );
    case 'apple':
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
          <path d="M16.36 12.78c-.02-2.06 1.68-3.05 1.76-3.1-0.96-1.4-2.45-1.6-2.98-1.62-1.27-.13-2.48.75-3.12.75-.64 0-1.64-.73-2.7-.71-1.39.02-2.67.81-3.38 2.05-1.44 2.5-.37 6.2 1.03 8.23.68.99 1.5 2.1 2.56 2.06 1.03-.04 1.42-.66 2.66-.66 1.24 0 1.59.66 2.68.64 1.11-.02 1.81-1.01 2.49-2.01.78-1.15 1.1-2.27 1.12-2.33-.02-.01-2.15-.83-2.17-3.27zM14.3 6.6c.56-.69.94-1.64.84-2.6-.81.03-1.8.54-2.38 1.22-.52.6-.98 1.57-.86 2.5.9.07 1.83-.46 2.4-1.12z" />
        </svg>
      );
    case 'email':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16">
          <rect x="3" y="6" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <path
            d="M 3 8 L 12 14 L 21 8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'guest':
    default:
      return (
        <svg viewBox="0 0 24 24" width="18" height="18">
          <path
            d="M 6 4 Q 6 2 12 2 Q 18 2 18 4 L 18 17 L 15 15 L 12 18 L 9 15 L 6 17 Z"
            fill="currentColor"
            opacity="0.85"
          />
          <circle cx="10" cy="9" r="1.4" fill="#0E0B2E" />
          <circle cx="14" cy="9" r="1.4" fill="#0E0B2E" />
        </svg>
      );
  }
}

function Spinner({ color }: { color: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      style={{ animation: 'spin-slow 0.8s linear infinite' }}
    >
      <circle cx="12" cy="12" r="9" fill="none" stroke={color} strokeWidth="2.5" opacity="0.2" />
      <path
        d="M 12 3 A 9 9 0 0 1 21 12"
        stroke={color}
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface FloatingHexProps {
  color: 'red' | 'blue' | 'yellow' | 'purple';
  top: string;
  left?: string;
  right?: string;
  size: number;
  delay: number;
}

function FloatingHex({ color, top, left, right, size, delay }: FloatingHexProps) {
  return (
    <div
      style={{
        position: 'absolute',
        top,
        left,
        right,
        animation: `climb-bob 3s ease-in-out ${delay}s infinite`,
        opacity: 0.55,
        zIndex: 1,
      }}
    >
      <Hold color={color} shape="hex" width={size} height={size} glow />
    </div>
  );
}
